import {parsePhoneNumber, type CountryCode} from 'libphonenumber-js';

/**
 * Result of parsing a phone number to E.164 format
 */
export interface PhoneE164Result {
  /** E.164 formatted phone number, e.g. "+972501234567" */
  e164: string;
  /** ISO 3166-1 alpha-2 country code, e.g. "IL" */
  country: string;
}

/**
 * Parse a phone string (any user-friendly format) and convert it to E.164.
 *
 * Accepts any format the user might type: "050-123-4567", "(050) 1234567",
 * "050 123 4567", "+972-50-1234567", etc.
 *
 * Returns null when the number cannot be parsed or is invalid.
 *
 * @param phone - Raw phone string in any format
 * @param defaultCountry - Country code to assume for local numbers (default: 'IL')
 *
 * @example
 * parsePhoneToE164('050-123-4567')        // { e164: '+972501234567', country: 'IL' }
 * parsePhoneToE164('+1 (800) 555-0100')   // { e164: '+18005550100', country: 'US' }
 * parsePhoneToE164('not-a-number')        // null
 */
/**
 * Format an E.164 phone number to national display format.
 *
 * @example
 * formatPhoneDisplay('+972541234567') // "054-1234567"
 */
export function formatPhoneDisplay(phone: string, defaultCountry: CountryCode = 'IL'): string {
  try {
    const parsed = parsePhoneNumber(phone, defaultCountry);
    if (!parsed.isValid()) return phone;
    return parsed.formatNational();
  } catch {
    return phone;
  }
}

export function parsePhoneToE164(
  phone: string,
  defaultCountry: CountryCode = 'IL'
): PhoneE164Result | null {
  try {
    const parsed = parsePhoneNumber(phone, defaultCountry);
    if (!parsed.isValid()) return null;
    return {
      e164: parsed.number, // e.g. "+972501234567"
      country: parsed.country ?? defaultCountry
    };
  } catch {
    return null;
  }
}

const ISRAELI_MOBILE_DIGITS = 10;

// Tolerates undefined/null despite the `string` type — RHF field values can violate it at runtime (e.g. sparse array fields).
const toDigits = (raw: string): string => (raw ?? '').replace(/\D/g, '');

/**
 * Formats raw Israeli mobile digits for display while typing. Caps at 10 digits — the
 * masking-while-typing cap for the digit-entry component lives here, not as a shared export,
 * since `isValidIsraeliMobile` below must NOT cap (a capped, silently-truncated overlong value
 * would wrongly validate).
 *
 * @example
 * formatIsraeliMobileDisplay('0501234567') // "050-1234567"
 * formatIsraeliMobileDisplay('05012')      // "050-12"
 */
export function formatIsraeliMobileDisplay(raw: string): string {
  const digits = toDigits(raw).slice(0, ISRAELI_MOBILE_DIGITS);
  return digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
}

/**
 * Validates a normalized (unformatted) Israeli mobile number stored as `0501234567`:
 * `05` prefix, 10 digits total, confirmed valid via libphonenumber-js.
 */
export function isValidIsraeliMobile(raw: string): boolean {
  const digits = toDigits(raw);
  if (!/^05\d{8}$/.test(digits)) return false;
  return parsePhoneToE164(digits, 'IL') !== null;
}

/**
 * Converts any parseable phone string — E.164 (as stored in the DB, e.g. `phone_e164`
 * columns), national, or already-local — to plain local digits (`"0501234567"`). Use when
 * hydrating a form field from a stored E.164 value for `PhoneNumberField`/`isValidIsraeliMobile`,
 * which both expect local digits, not E.164.
 *
 * @example
 * normalizePhoneToLocalDigits('+972501234567') // "0501234567"
 */
export function normalizePhoneToLocalDigits(
  phone: string,
  defaultCountry: CountryCode = 'IL'
): string {
  return formatPhoneDisplay(phone, defaultCountry).replace(/\D/g, '');
}
